// src/pages/AnomalyDetectionInterface.js

import React, { useState, useRef, useEffect, useMemo } from 'react';
import JSZip from 'jszip';
import AnalysisModal from '../components/AnalysisModal';
import ThresholdModal from '../components/ThresholdModal';
import MeansModal from '../components/MeansModal';
import { useAuth } from '../context/AuthContext';
import { useTutorial } from '../context/TutorialContext';
import supabase from '../supabaseClient';
import styles from '../style/AnomalyDetectionInterface.module.css';
import { saveFiles, getAllFiles, clearAllFiles, deleteFile } from '../utils/indexedDB';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

// --- MUI Icon Imports ---
import FileUploadIcon from '@mui/icons-material/FileUpload';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import CalculateIcon from '@mui/icons-material/Calculate';
import SettingsIcon from '@mui/icons-material/Settings';
import ScienceIcon from '@mui/icons-material/Science';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DeselectIcon from '@mui/icons-material/Deselect';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

const APP_STATE_KEY = 'anomalyDetectionState';
const SESSION_FLAG_KEY = 'appSessionActive';

// --- STATIC TRANSLATIONS (AWN DEFAULTS) ---
// These are used automatically if the sensor ID (e.g., IW1) matches,
// ensuring AWN files have correct names by default.
const STATIC_SENSOR_TRANSLATIONS = {
  "IW1": "Wasserablassventil links", "IW2": "Wasserablassventil rechts", "IW3": "Speisewassertank Füllstand",
  "IW4": "Speisewassertank Temperatur", "IW5": "Speisewasserpumpe Sollwert FU", "IW6": "Speisewasser Druck vor Pumpe",
  "IW7": "VDL Speisewasserventil Stellung", "IW8": "VDL Abgaseintrittstemperatur", "IW9": "VDL Abgasaustrittstemperatur",
  "IW10": "VDL Abgasgegendruck", "IW11": "VDL Dampfaustrittstemperatur", "IW12": "VDL_x Speisewasserventil Stellung",
  "IW13": "VDL_x Abgaseintrittstemperatur", "IW14": "VDL_x Abgasaustrittstemperatur", "IW15": "VDL_x Abgasgegendruck",
  "IW16": "VDL_x Dampfaustrittstemperatur", "IW17": "VDR Speisewasserventil Stellung", "IW18": "VDR Abgaseintrittstemperatur",
  "IW19": "VDR Abgasaustrittstemperatur", "IW20": "VDR Abgasgegendruck", "IW21": "VDR Dampfaustrittstemperatur",
  "IW22": "Generatorleistung", "IW23": "Dampftemperatur vor Expander", "IW24": "Dampfdruck vor Expander",
  "IW25": "Öldruck Expander", "IW26": "Öltemperatur Expander", "IW27": "Expander läuft", "IW28": "Generatordrehzahl",
  "IW29": "Kühlkreis Kondensatoraustrittstemperatur", "IW30": "Sollwert Leistungsregelung Dampf",
  "IW31": "VDR_x Speisewasserventil Stellung", "IW32": "VDR_x Abgaseintrittstemperatur", "IW33": "VDR_x Abgasaustrittstemperatur",
  "IW34": "VDR_x Abgasgegendruck", "IW35": "VDR_x Dampfaustrittstemperatur", "IW36": "Öl Schauglastemperatur",
  "IW37": "Ölpumpe läuft", "IW38": "VDR Abgas Steilheit", "IW39": "VDL Abgas Steilheit", "IW40": "VDL_x Abgas Steilheit",
  "IW41": "VDR_x Abgas Steilheit", "IW42": "Dampf vor Expander Temp.Mittelwert_x_Zykl"
};

function base64ToFile(base64String, filename) {
  try {
    const byteString = atob(base64String);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab]);
    return new File([blob], filename);
  } catch (error) {
    console.error("Fehler bei der Konvertierung von Base64 in Datei:", error);
    return null;
  }
}

function AnomalyDetectionInterface() {
  const { user } = useAuth();
  const [loadedFiles, setLoadedFiles] = useState([]);
  const [activeFile, setActiveFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [resultCache, setResultCache] = useState({});
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('Bereit. Bitte importieren Sie eine Datei, um zu beginnen.');
  const [progress, setProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSensors, setSelectedSensors] = useState(new Set());
  const [sensorColumns, setSensorColumns] = useState([]);
  const [anomalousSensors, setAnomalousSensors] = useState([]);
  const [isControlsOpen, setIsControlsOpen] = useState(true);
  const [showPlotControls, setShowPlotControls] = useState(true);
  const lastPlotScrollY = useRef(0);

  const handlePlotAreaScroll = (e) => {
    const currentScrollY = e.target.scrollTop;
    
    if (currentScrollY > lastPlotScrollY.current && currentScrollY > 40) {
      if (showPlotControls) setShowPlotControls(false);
    } 
    else if (currentScrollY < lastPlotScrollY.current) {
      if (!showPlotControls) setShowPlotControls(true);
    }
    
    lastPlotScrollY.current = currentScrollY;
  };

  // Modals
  const [isAnalysisModalOpen, setAnalysisModalOpen] = useState(false);
  const [isThresholdModalOpen, setThresholdModalOpen] = useState(false);
  const [isMeansModalOpen, setMeansModalOpen] = useState(false);
  const [deleteDialogFile, setDeleteDialogFile] = useState(null);
  const [customMessageDialog, setCustomMessageDialog] = useState({
    open: false,
    title: '',
    message: '',
  });

  // Data & Settings
  const [savedThresholds, setSavedThresholds] = useState(null);
  const [userMappings, setUserMappings] = useState({}); // Custom names from DB

  const [modalPlotState, setModalPlotState] = useState({
    isLoading: false,
    plotUrl: '',
    legendData: [],
    viewHistory: [],
    historyIndex: -1,
    sensorsToPlot: new Set(),
    interactionMode: 'zoom',
    y2Limit: 40,
    error: ''
  });

  const fileInputRef = useRef(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(250);
  const [isResizing, setIsResizing] = useState(false);
  const leftPanelRef = useRef(null);

  const { joyrideState, startTutorial, goToNextStep } = useTutorial();
  const isTutorialActive = joyrideState.run && joyrideState.tutorialKey.includes('anomaly');
  const isNewUserForTutorial = isTutorialActive && loadedFiles.length === 0;
  const apiBase = (process.env.REACT_APP_API_URL || '/api').replace(/\/+$/, '');

  const fakeTutorialSensors = [
    { name: 'IW1' }, { name: 'IW8' }, { name: 'IW11' }, { name: 'IW22' }, { name: 'IW24' }
  ];

  // on mobile default collapsed (unless tutorial is active), on desktop always open
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');

    const apply = () => {
      if (mq.matches) {
        setIsControlsOpen(isTutorialActive ? true : false);
      } else {
        setIsControlsOpen(true);
      }
    };

    apply();

    if (mq.addEventListener) {
      mq.addEventListener('change', apply);
      return () => mq.removeEventListener('change', apply);
    } else {
      mq.addListener(apply);
      return () => mq.removeListener(apply);
    }
  }, [isTutorialActive]);

  // --- 1. SENSOR NAME LOGIC ---
  // Merge Strategy: User Mappings > AWN Defaults > Empty
  const mergedTranslations = useMemo(() => {
    return { ...STATIC_SENSOR_TRANSLATIONS, ...userMappings };
  }, [userMappings]);

  // --- 2. FETCH USER NAMES ---
  useEffect(() => {
  if (user?.id) {
    fetch(`${apiBase}/sensor-mappings?user_id=${user.id}`)
      .then(async (res) => {
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data?.error || 'Sensor mappings could not be loaded');
        }
        return data;
      })
      .then((data) => {
        setUserMappings(data || {});
      })
      .catch((err) => {
        console.error("Sensor mappings could not be loaded:", err.message || err);
        setUserMappings({});
      });
  }
}, [user, apiBase]);


  // --- 3. SAVE NEW NAMES ---
  const handleRenameSensor = async (sensorId, newName) => {
  if (!user?.id) return;

  const previousMappings = { ...userMappings };
  const updatedMappings = { ...userMappings, [sensorId]: newName };
  setUserMappings(updatedMappings);

  try {
    const response = await fetch(`${apiBase}/sensor-mappings`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        user_id: user.id,
        mappings: updatedMappings
      })
    });

    if (!response.ok) {
      let message = 'Name konnte nicht gespeichert werden.';
      try {
        const errJson = await response.json();
        if (errJson?.error) message = errJson.error;
      } catch (_) {}
      throw new Error(message);
    }
  } catch (err) {
    console.error("Fehler beim Speichern des Sensor-Namens:", err);
    setUserMappings(previousMappings);
    setError(err.message || "Name konnte nicht gespeichert werden.");
  }
};


  // --- Resize Logic ---
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const handleResizeMouseMove = (e_move) => {
      setLeftPanelWidth(prevWidth => {
        const newWidth = prevWidth + e_move.movementX;
        const minWidth = 200;
        const parentWidth = leftPanelRef.current?.parentElement?.clientWidth;
        const maxWidth = parentWidth ? parentWidth * 0.7 : window.innerWidth * 0.7;
        if (newWidth < minWidth) return minWidth;
        if (newWidth > maxWidth) return maxWidth;
        return newWidth;
      });
    };
    const handleResizeMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleResizeMouseMove);
      document.removeEventListener('mouseup', handleResizeMouseUp);
    };
    document.addEventListener('mousemove', handleResizeMouseMove);
    document.addEventListener('mouseup', handleResizeMouseUp);
  };

  // ... (Standard session restore logic) ...
  useEffect(() => {
    const restoreState = async () => {
      setIsLoading(true);
      const isReload = sessionStorage.getItem(SESSION_FLAG_KEY);

      if (isReload) {
        console.log("Sitzung wird wiederhergestellt...");
        try {
          const filesFromDB = await getAllFiles();
          const restoredLoadedFiles = filesFromDB.map(file => ({ name: file.name, fileObject: file }));
          setLoadedFiles(restoredLoadedFiles);

          const savedStateJSON = sessionStorage.getItem(APP_STATE_KEY);
          if (savedStateJSON) {
            const savedState = JSON.parse(savedStateJSON);

            if (savedState.activeFileName && restoredLoadedFiles.length > 0) {
              const foundFile = restoredLoadedFiles.find(f => f.name === savedState.activeFileName);
              if (foundFile) {
                setActiveFile(foundFile);
                setStatusMessage(`Aktive Datei: ${foundFile.name}`);
              }
            }

            if (savedState.analysisResult) setAnalysisResult(savedState.analysisResult);
            if (savedState.resultCache) setResultCache(savedState.resultCache);
            if (savedState.selectedSensors) setSelectedSensors(new Set(savedState.selectedSensors));
            if (savedState.leftPanelWidth) setLeftPanelWidth(savedState.leftPanelWidth);
          }
        } catch (err) {
          console.error("Konnte den App-Status nicht wiederherstellen:", err);
          setError("Fehler beim Laden der gespeicherten Sitzung. Bitte importieren Sie Ihre Dateien erneut.");
          sessionStorage.removeItem(APP_STATE_KEY);
          await clearAllFiles();
        } finally {
          setIsLoading(false);
        }

      } else {
        console.log("Neue Sitzung erkannt. Alte Daten werden gelöscht...");
        try {
          await clearAllFiles();
        } catch (err) {
          console.error("Fehler beim Löschen der IndexedDB:", err);
        } finally {
          sessionStorage.setItem(SESSION_FLAG_KEY, 'true');
          setLoadedFiles([]);
          setActiveFile(null);
          setAnalysisResult(null);
          setResultCache({});
          setSelectedSensors(new Set());
          setLeftPanelWidth(250);
          setStatusMessage('Bereit. Bitte importieren Sie eine Datei, um zu beginnen.');
          setIsLoading(false);
        }
      }
    };
    restoreState();
  }, []);

  useEffect(() => {
    try {
      const stateToSave = {
        activeFileName: activeFile ? activeFile.name : null,
        analysisResult,
        resultCache,
        selectedSensors: Array.from(selectedSensors),
        leftPanelWidth,
      };
      sessionStorage.setItem(APP_STATE_KEY, JSON.stringify(stateToSave));
    } catch (err) {
      console.error("Konnte App-Status nicht im Session Storage speichern", err);
    }
  }, [activeFile, analysisResult, resultCache, selectedSensors, leftPanelWidth]);

  useEffect(() => {
    startTutorial('anomaly', true);
  }, [startTutorial]);

  // --- Plotting & Analysis Functions (unchanged) ---
  const fetchAnalysisPlot = async (viewState, sensors, limit, options = {}) => {
    const { isNew = false, isHistoryChange = false } = options;
    if (!activeFile || !activeFile.fileObject || sensors.size === 0) {
      setModalPlotState(prev => ({ ...prev, plotUrl: '', legendData: [], viewHistory: [], historyIndex: -1 }));
      return;
    }
    setModalPlotState(prev => ({ ...prev, isLoading: true, error: '' }));
    const formData = new FormData();
    formData.append('file', activeFile.fileObject, activeFile.name);
    formData.append('sensors', JSON.stringify(Array.from(sensors)));
    formData.append('y2_limit', limit);
    if (viewState) {
      formData.append('view_state', JSON.stringify(viewState));
    }
    try {
      const response = await fetch(`${apiBase}/combined-plot`, { method: 'POST', body: formData });
      if (!response.ok) throw new Error((await response.json()).error || 'Fehler beim Laden des Plots');
      const result = await response.json();
      const newViewState = result.view_state;
      setModalPlotState(prev => {
        let newHistory = prev.viewHistory;
        let newHistoryIndex = prev.historyIndex;
        if (isHistoryChange) {
        } else if (isNew) {
          newHistory = [...prev.viewHistory.slice(0, prev.historyIndex + 1), newViewState];
          newHistoryIndex = newHistory.length - 1;
        } else {
          newHistory = [newViewState];
          newHistoryIndex = 0;
        }
        return {
          ...prev,
          plotUrl: result.plot_image_data,
          legendData: result.legend_data || [],
          viewHistory: newHistory,
          historyIndex: newHistoryIndex
        };
      });
    } catch (err) {
      setModalPlotState(prev => ({ ...prev, error: err.message }));
    } finally {
      setModalPlotState(prev => ({ ...prev, isLoading: false }));
    }
  };

  const handlePlotInteraction = (newViewState) => {
    fetchAnalysisPlot(newViewState, modalPlotState.sensorsToPlot, modalPlotState.y2Limit, { isNew: true });
  };

  const handlePlotHome = () => {
    fetchAnalysisPlot(null, modalPlotState.sensorsToPlot, modalPlotState.y2Limit);
  };

  const handlePlotBack = () => {
    if (modalPlotState.historyIndex > 0) {
      const newIndex = modalPlotState.historyIndex - 1;
      setModalPlotState(prev => ({ ...prev, historyIndex: newIndex }));
      fetchAnalysisPlot(modalPlotState.viewHistory[newIndex], modalPlotState.sensorsToPlot, modalPlotState.y2Limit, { isHistoryChange: true });
    }
  };

  const handlePlotForward = () => {
    if (modalPlotState.historyIndex < modalPlotState.viewHistory.length - 1) {
      const newIndex = modalPlotState.historyIndex + 1;
      setModalPlotState(prev => ({ ...prev, historyIndex: newIndex }));
      fetchAnalysisPlot(modalPlotState.viewHistory[newIndex], modalPlotState.sensorsToPlot, modalPlotState.y2Limit, { isHistoryChange: true });
    }
  };

  const handleModalSensorChange = (sensorName) => {
    const newSelection = new Set(modalPlotState.sensorsToPlot);
    newSelection.has(sensorName) ? newSelection.delete(sensorName) : newSelection.add(sensorName);
    setModalPlotState(prev => ({ ...prev, sensorsToPlot: newSelection }));
    fetchAnalysisPlot(null, newSelection, modalPlotState.y2Limit);
  };

  const handleY2LimitChange = (newLimit) => {
    setModalPlotState(prev => ({ ...prev, y2Limit: newLimit }));
  };

  const handleY2LimitApply = () => {
    fetchAnalysisPlot(null, modalPlotState.sensorsToPlot, modalPlotState.y2Limit);
  };

  const handleInteractionModeChange = (newMode) => {
    setModalPlotState(prev => ({ ...prev, interactionMode: newMode }));
  };

  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;
    setIsLoading(true);
    setStatusMessage('Verarbeite und speichere importierte Dateien...');
    const newFileObjects = [];
    const filesToSaveToDB = [];
    const jszip = new JSZip();
    for (const file of selectedFiles) {
      if (file.name.toLowerCase().endsWith('.zip')) {
        try {
          const zip = await jszip.loadAsync(file);
          for (const originalPath in zip.files) {
            const zipEntry = zip.files[originalPath];
            if (zipEntry.dir || originalPath.startsWith('__MACOSX/')) continue;
            const fileContent = await zipEntry.async('blob');
            const newFilename = originalPath.split('/').pop();
            const extractedFile = new File([fileContent], newFilename, { type: fileContent.type });
            newFileObjects.push({ name: extractedFile.name, fileObject: extractedFile });
            filesToSaveToDB.push(extractedFile);
          }
        } catch (e) {
          console.error("Fehler beim Entpacken der Datei:", file.name, e);
          setError(`Entpacken fehlgeschlagen: ${file.name}`);
        }
      } else {
        newFileObjects.push({ name: file.name, fileObject: file });
        filesToSaveToDB.push(file);
      }
    }
    try {
      await saveFiles(filesToSaveToDB);
      const filesMap = new Map(loadedFiles.map(f => [f.name, f]));
      newFileObjects.forEach(f => filesMap.set(f.name, f));
      setLoadedFiles(Array.from(filesMap.values()));
      if (newFileObjects.length > 0) selectFile(newFileObjects[0]);
      setStatusMessage(`${newFileObjects.length} Datei(en) geladen und bereit.`);
    } catch (err) {
      console.error("Fehler beim Speichern der Dateien:", err);
      setError("Konnte eine oder mehrere Dateien nicht im Browser speichern.");
    }
    setIsLoading(false);
    if (fileInputRef.current) { fileInputRef.current.value = ""; }
  };

  const handleDeleteFile = (e, fileToDelete) => {
    e.stopPropagation();
    setDeleteDialogFile(fileToDelete);
  };

  const confirmDeleteFile = async () => {
    if (!deleteDialogFile) return;

    const fileToDelete = deleteDialogFile;
    setDeleteDialogFile(null);

    try {
      await deleteFile(fileToDelete.name);
      setLoadedFiles(prevFiles => prevFiles.filter(f => f.name !== fileToDelete.name));
      if (activeFile?.name === fileToDelete.name) {
        setActiveFile(null);
        setAnalysisResult(null);
        setSelectedSensors(new Set());
        setSensorColumns([]);
        setStatusMessage('Datei gelöscht. Bitte wählen Sie eine andere Datei.');
      } else {
        setStatusMessage(`${fileToDelete.name} gelöscht.`);
      }
      const pctKey = `${fileToDelete.name}-percentage`;
      const lhKey = `${fileToDelete.name}-lowhigh`;
      setResultCache(prevCache => {
        const newCache = { ...prevCache };
        delete newCache[pctKey];
        delete newCache[lhKey];
        return newCache;
      });
    } catch (err) {
      console.error('Löschen fehlgeschlagen:', err);
      setError(`Konnte ${fileToDelete.name} nicht löschen.`);
    }
  };

  const handleImportClick = () => fileInputRef.current.click();

  const performAutoAnalysis = async (files) => {
    if (!files || files.length === 0) return;

    setStatusMessage(`Analysiere ${files.length} neue Datei(en) für das Dashboard...`);

    const formData = new FormData();
    files.forEach(file => {
      formData.append('files[]', file, file.name);
    });

    const defaultThresholds = {};
    formData.append('thresholds', JSON.stringify(defaultThresholds));
    formData.append('user_id', user.id);

    try {
      const response = await fetch(`${apiBase}/generate-excel-report`, { method: 'POST', body: formData });
      const result = await response.json();

      if (!response.ok) {
        console.error("Auto-Analyze failed", result);
        setError("Automatische Dashboard-Analyse fehlgeschlagen: " + (result.error || "Serverfehler"));
      } else {
        let successMsg = `${files.length} neue Datei(en) analysiert und im Dashboard gespeichert.`;
        if (result.skipped_files && result.skipped_files.length > 0) {
          successMsg += ` (Übersprungen: ${result.skipped_files.join(', ')})`;
        }
        setStatusMessage(successMsg);
      }
    } catch (err) {
      console.error(err);
      setError("Netzwerkfehler bei der automatischen Analyse.");
    }
  };

  const handleSyncFromCloud = async () => {
    if (!user) {
      alert("Bitte loggen Sie sich ein, um von Ihrem Cloud-Speicher zu synchronisieren.");
      return;
    }
    setIsLoading(true);
    setStatusMessage("Verbinde mit Cloud-Speicher...");
    setError('');

    try {
      const response = await fetch(`${apiBase}/sync-from-cloud`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id }),
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Cloud-Synchronisierung fehlgeschlagen.');

      let finalMessage = result.message || 'Cloud-Synchronisierung erfolgreich abgeschlossen.';

      if (result.new_files && result.new_files.length > 0) {
        setStatusMessage(`Synchronisierung fertig. Lade ${result.new_files.length} neue Datei(en)...`);

        const filesToSaveToDB = result.new_files
          .map(fileData => base64ToFile(fileData.content, fileData.name))
          .filter(file => file !== null);

        const newFileObjects = filesToSaveToDB.map(file => ({
          name: file.name,
          fileObject: file
        }));

        await saveFiles(filesToSaveToDB);

        const filesMap = new Map(loadedFiles.map(f => [f.name, f]));
        newFileObjects.forEach(f => filesMap.set(f.name, f));
        setLoadedFiles(Array.from(filesMap.values()));

        if (newFileObjects.length > 0) {
          selectFile(newFileObjects[0]);
        }

        await performAutoAnalysis(filesToSaveToDB);

        finalMessage = `${newFileObjects.length} neue Datei(en) geladen und im Dashboard aktualisiert.`;
      }

      setStatusMessage(finalMessage);

      if (result.skipped_files && result.skipped_files.length > 0) {
        setError(`Warnung: Einige Dateien wurden übersprungen: ${result.skipped_files.join(', ')}.`);
      }

    } catch (err) {
      setStatusMessage(`Fehler: ${err.message}`);
      setError(`Cloud-Synchronisierung fehlgeschlagen: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const selectFile = (file) => {
    setActiveFile(file); setSensorColumns([]); setSavedThresholds(null);
    const pctKey = `${file.name}-percentage`; const lhKey = `${file.name}-lowhigh`;
    if (resultCache[pctKey] || resultCache[lhKey]) {
      const cachedResult = resultCache[pctKey] || resultCache[lhKey];
      setAnalysisResult(cachedResult); setAnomalousSensors(cachedResult.anomalous_sensors || []);
      setStatusMessage(`Aktive Datei: ${file.name} (Zwischengespeichert)`);
    } else {
      setAnalysisResult(null); setAnomalousSensors([]);
      setStatusMessage(`Aktive Datei: ${file.name}`);
    }
    setSelectedSensors(new Set()); setError(''); setProgress(0);
  };

  const saveThresholdsToDatabase = async (type, thresholds) => {
    if (!user || !activeFile) return;
    const { data: existingData } = await supabase.from('user_thresholds').select('thresholds').eq('user_id', user.id).eq('file_name', activeFile.name).single();
    const newThresholds = { ...(existingData?.thresholds || {}), [type]: thresholds };
    const { error } = await supabase.from('user_thresholds').upsert({ user_id: user.id, file_name: activeFile.name, thresholds: newThresholds, updated_at: new Date() }, { onConflict: 'user_id, file_name' });
    if (error) { console.error('Fehler beim Speichern der Grenzwerte:', error); setError('Konnte Ihre Grenzwerte nicht speichern.'); }
    else { console.log('Grenzwerte erfolgreich gespeichert.'); }
  };

  const handleAnalysisWithThresholds = async (type, thresholds) => {
    saveThresholdsToDatabase(type, thresholds);
    if (!activeFile || !activeFile.fileObject) return;
    if (type === 'percentage') {
      setIsLoading(true); setError(''); setProgress(0); setStatusMessage('Starte Analyse...'); setAnalysisResult(null);
      const endpoint = '/analyze'; const formData = new FormData();
      formData.append('file', activeFile.fileObject, activeFile.name); 
      formData.append('thresholds', JSON.stringify(thresholds));
      try {
        const response = await fetch(`${apiBase}${endpoint}`, { method: 'POST', body: formData });
        if (!response.ok || !response.body) {
          const errData = await response.json().catch(() => ({ error: 'Ein unbekannter Fehler ist aufgetreten' }));
          throw new Error(errData.error || `Server antwortete mit ${response.status}`);
        }
        const reader = response.body.getReader(); const decoder = new TextDecoder(); let buffer = '';
        while (true) {
          const { done, value } = await reader.read(); if (done) break; buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n'); buffer = lines.pop();
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonData = line.substring(6); const data = JSON.parse(jsonData);
              if (data.type === 'progress') { setProgress(data.value); setStatusMessage(data.message); }
              else if (data.type === 'result') {
                const result = data.payload; const formattedResult = { plots: result.plots.map(p => ({ ...p })) };
                setAnalysisResult(formattedResult); setAnomalousSensors(result.anomalous_sensors || []);
                const cacheKey = `${activeFile.name}-${type}`;
                setResultCache(prev => ({ ...prev, [cacheKey]: { ...formattedResult, anomalous_sensors: result.anomalous_sensors || [] } }));
              }
            }
          }
        }
      } catch (err) { setError(`Analyse fehlgeschlagen: ${err.message}`); setProgress(0); setStatusMessage('Fehler während der Analyse.'); }
      finally { setIsLoading(false); setTimeout(() => { if (!error) { setStatusMessage(`Analyse abgeschlossen für ${activeFile.name}.`); } }, 4000); }
    } else {
      setIsLoading(true); setError(''); setProgress(10); setStatusMessage('Starte Low/High-Analyse...');
      const endpoint = '/analyze-low-high'; const cacheKey = `${activeFile.name}-${type}`;
      const formData = new FormData(); 
      formData.append('file', activeFile.fileObject, activeFile.name); 
      formData.append('thresholds', JSON.stringify(thresholds));
      try {
        const response = await fetch(`${apiBase}${endpoint}`, { method: 'POST', body: formData });
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Analyse fehlgeschlagen.');
        const formattedResult = { plots: result.plots.map(p => ({ ...p })) };
        setAnalysisResult(formattedResult);
        setAnomalousSensors(result.anomalous_sensors || []);
        setResultCache(prev => ({ ...prev, [cacheKey]: { ...formattedResult, anomalous_sensors: result.anomalous_sensors || [] } }));
        setProgress(100);
        setStatusMessage(`Low/High-Analyse abgeschlossen für ${activeFile.name}.`);
      } catch (err) {
        setError(`Analyse fehlgeschlagen: ${err.message}`);
        setProgress(0);
        setStatusMessage('Fehler während der Analyse.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const ensureColumnsAndThresholdsAreLoaded = async () => {
    if (!activeFile || !user) return false;

    // --- NEW SAFETY CHECK ---
    // Prevent sending fake tutorial files or empty objects to the backend
    if (!activeFile.fileObject) {
      setError("Diese Datei enthält keine echten Daten (z. B. Tutorial-Datei). Bitte importieren Sie eine reale Datei.");
      return false;
    }
    // ------------------------

    setIsLoading(true); setStatusMessage("Lade Spalten und Grenzwerte...");
    try {
      const formData = new FormData();
      formData.append('file', activeFile.fileObject, activeFile.name);
      const response = await fetch(`${apiBase}/get-columns`, { method: 'POST', body: formData });
      if (!response.ok) {
        const errData = await response.json(); throw new Error(errData.error || 'Konnte Spalten nicht vom Server laden.');
      }
      const data = await response.json(); setSensorColumns(data.columns || []);
      const { data: thresholdData, error } = await supabase.from('user_thresholds').select('thresholds').eq('user_id', user.id).eq('file_name', activeFile.name).single();
      if (error && error.code !== 'PGRST116') { throw new Error(`Supabase Fehler beim Laden der Grenzwerte: ${error.message}`); }
      setSavedThresholds(thresholdData ? thresholdData.thresholds : null);
      return true;
    } catch (err) {
      console.error("Fehler in ensureColumnsAndThresholdsAreLoaded:", err);
      setError(`Fehler beim Laden der Voraussetzungen: ${err.message}.`);
      return false;
    } finally { setIsLoading(false); setStatusMessage("Bereit."); }
  };

  const handleOpenThresholdModal = async () => {
    const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="anomaly-analyze-button"]';

    if (isTutorialTrigger && isNewUserForTutorial) {
      setSensorColumns(fakeTutorialSensors);
      setSavedThresholds(null);
      setThresholdModalOpen(true);
      goToNextStep();
      return;
    }

    const success = await ensureColumnsAndThresholdsAreLoaded();
    if (success) {
      setThresholdModalOpen(true);
      if (isTutorialTrigger) {
        goToNextStep();
      }
    }
  };

  const handleOpenAnalysisModal = async () => {
    const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="anomaly-analyze-sensors-button"]';

    if (isTutorialTrigger && isNewUserForTutorial) {
      if (sensorColumns.length === 0) setSensorColumns(fakeTutorialSensors);
      setAnomalousSensors(['IW8', 'IW22']);
      setModalPlotState(prev => ({
        ...prev,
        sensorsToPlot: new Set(['IW8', 'IW22']),
        plotUrl: 'https://via.placeholder.com/600x400.png?text=Sample+Sensor+Plot',
        legendData: [
          { name: 'IW8', color: '#007bff' },
          { name: 'IW22', color: '#ff7c00' }
        ],
        viewHistory: [{ ax_bbox: [0.1, 0.1, 0.8, 0.8], xlim: [0, 1], ylim: [0, 1] }],
        historyIndex: 0,
        isLoading: false,
        error: ''
      }));
      setAnalysisModalOpen(true);
      goToNextStep();
      return;
    }

    if (!activeFile) {
      alert("Bitte wählen Sie eine Datei zur Analyse aus.");
      return;
    }
    if (sensorColumns.length === 0) {
      const success = await ensureColumnsAndThresholdsAreLoaded();
      if (success === false) return;
    }
    const sensorsToPlotInitially = new Set(selectedSensors);
    setModalPlotState(prev => ({
      ...prev,
      sensorsToPlot: sensorsToPlotInitially,
      plotUrl: '', legendData: [], viewHistory: [], historyIndex: -1, error: ''
    }));
    if (sensorsToPlotInitially.size > 0) {
      fetchAnalysisPlot(null, sensorsToPlotInitially, modalPlotState.y2Limit);
    }
    setAnalysisModalOpen(true);
    if (isTutorialTrigger) {
      goToNextStep();
    }
  };

  const handleDownloadReport = async () => {
    if (!analysisResult?.plots?.length) { alert("Keine Analyseergebnisse für die Berichterstellung verfügbar."); return; }
    setStatusMessage("Erstelle PDF-Bericht..."); setIsLoading(true); setError('');
    try {
      const response = await fetch(`${apiBase}/generate-report`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ plots: analysisResult.plots }) });
      if (!response.ok) throw new Error(`Server antwortete mit Status: ${response.status}`);
      const blob = await response.blob(); const url = window.URL.createObjectURL(blob);
      const dateStr = new Date().toISOString().slice(0, 10);
      const fileNamePrefix = activeFile ? activeFile.name.split('.')[0] : 'Anomalie';
      const fileName = `Anomaliebericht_${fileNamePrefix}_${dateStr}.pdf`;
      const a = document.createElement('a'); a.href = url; a.download = fileName;
      document.body.appendChild(a); a.click(); window.URL.revokeObjectURL(url); a.remove();
      setStatusMessage(`PDF-Bericht erfolgreich heruntergeladen: ${fileName}`);
    } catch (err) { setError("Konnte den Bericht nicht herunterladen. " + err.message); }
    finally { setIsLoading(false); }
  };

  const handleAutoGenerateExcelReport = async () => {
    if (loadedFiles.length === 0) { alert("Bitte importieren Sie mindestens eine Datei, um einen Bericht zu erstellen."); return; }
    if (!activeFile) { alert("Bitte wählen Sie zuerst eine aktive Datei aus."); return; }
    setIsLoading(true); setStatusMessage("Lade Grenzwerte und erstelle Bericht...");
    const fetchedThresholds = await ensureColumnsAndThresholdsAreLoaded();
    let lowHighThresholds = fetchedThresholds?.lowhigh;
    if (!lowHighThresholds) {
      console.log("Keine gespeicherten Low/High-Grenzwerte gefunden. Fahre mit Standardwerten fort.");
      lowHighThresholds = {};
    }
    setStatusMessage("Erstelle und speichere Bericht für alle geladenen Dateien...");
    const formData = new FormData();
    loadedFiles.forEach(file => { formData.append('files[]', file.fileObject, file.name); });
    formData.append('thresholds', JSON.stringify(lowHighThresholds)); formData.append('user_id', user.id);
    try {
      const response = await fetch(`${apiBase}/generate-excel-report`, { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) { throw new Error(result.error || 'Fehler beim Erstellen des Berichts auf dem Server.'); }
      let finalMessage = result.message || 'Excel-Bericht erfolgreich im Dashboard gespeichert!';
      if (result.skipped_files && result.skipped_files.length > 0) {
        finalMessage += ` Warnung: Einige Dateien wurden übersprungen: ${result.skipped_files.join(', ')}.`;
      }
      setStatusMessage(finalMessage);
      setCustomMessageDialog({
        open: true,
        title: 'Excel-Bericht gespeichert',
        message: `Der Excel-Bericht wurde erstellt und in Ihrem Dashboard-Bereich auf dem Server gespeichert.\n\nPfad: ${result.file_path || 'Standard-Ausgabeverzeichnis'}`
      });

    } catch (err) { setStatusMessage(`Fehler: ${err.message}`); }
    finally { setIsLoading(false); }
  };

  const handleSensorSelectionChange = (event, sensorName) => {
    event.stopPropagation();
    event.preventDefault();
    const newSelected = new Set(selectedSensors);
    newSelected.has(sensorName) ? newSelected.delete(sensorName) : newSelected.add(sensorName);
    setSelectedSensors(newSelected);
  };

  const handleSingleSensorAnalysis = (sensorName) => {
    if (activeFile && activeFile.fileObject) {
      const newSensorsToPlot = new Set([sensorName]);
      setModalPlotState(prev => ({
        ...prev, sensorsToPlot: newSensorsToPlot,
        plotUrl: '', legendData: [], viewHistory: [], historyIndex: -1, error: ''
      }));
      fetchAnalysisPlot(null, newSensorsToPlot, modalPlotState.y2Limit);
      setAnalysisModalOpen(true);
    } else {
      alert("Dateidaten nicht verfügbar. Bitte wählen Sie eine aktive Datei.");
    }
  };

  const handleSelectAllAnomalies = () => {
    if (analysisResult?.plots) {
      const allSensors = analysisResult.plots.map(p => p.sensor_name);
      setSelectedSensors(new Set(allSensors));
    }
  };

  const handleClearSelection = () => {
    setSelectedSensors(new Set());
  };

  const filteredFiles = loadedFiles.filter(file => file.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const iconStyle = { marginRight: '6px', fontSize: '1.15rem' };
  const smallIconStyle = { marginRight: '5px', fontSize: '0.9rem' };

  const dialogPaperStyle = {
    borderRadius: '16px',
    padding: '8px',
    backgroundColor: 'var(--modal-bg)',
    color: 'var(--text-color)',
    border: '1px solid var(--border-color)',
    boxShadow: 'var(--elevated-shadow)',
  };

  return (
    <div className={`${styles.container} ${isResizing ? styles.resizing : ''}`}>
      {/* --- Updated Threshold Modal: Passes rename handler --- */}
      <ThresholdModal
        isOpen={isThresholdModalOpen}
        onClose={() => {
          setThresholdModalOpen(false);
          const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="threshold-close"]';
          if (isTutorialTrigger) {
            goToNextStep();
          }
        }}
        onSave={handleAnalysisWithThresholds}
        activeFile={activeFile || (isNewUserForTutorial ? { name: 'sample_data.csv' } : null)}
        sensorColumns={sensorColumns}
        savedThresholds={savedThresholds}
        sensorTranslations={mergedTranslations}
        onRenameSensor={handleRenameSensor}
      />

      {/* --- Updated Means Modal: Passes translations --- */}
      <MeansModal
        isOpen={isMeansModalOpen}
        onClose={() => setMeansModalOpen(false)}
        activeFile={activeFile}
        sensorTranslations={mergedTranslations}
      />

      <AnalysisModal
        isOpen={isAnalysisModalOpen}
        onClose={() => {
          setAnalysisModalOpen(false);
          const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="analysis-close"]';
          if (isTutorialTrigger) {
            goToNextStep();
          }
        }}
        isLoading={modalPlotState.isLoading}
        plotUrl={modalPlotState.plotUrl}
        legendData={modalPlotState.legendData}
        error={modalPlotState.error}
        y2Limit={modalPlotState.y2Limit}
        sensorsToPlot={modalPlotState.sensorsToPlot}
        viewHistory={modalPlotState.viewHistory}
        historyIndex={modalPlotState.historyIndex}
        interactionMode={modalPlotState.interactionMode}
        allSensors={sensorColumns}
        anomalousSensors={anomalousSensors}
        activeFile={activeFile || (isNewUserForTutorial ? { name: 'sample_data.csv' } : null)}
        sensorTranslations={mergedTranslations}
        onSensorChange={handleModalSensorChange}
        onY2LimitChange={handleY2LimitChange}
        onApplyY2Limit={handleY2LimitApply}
        onInteraction={handlePlotInteraction}
        onHome={handlePlotHome}
        onBack={handlePlotBack}
        onForward={handlePlotForward}
        onModeChange={handleInteractionModeChange}
      />

      <input
        type="file"
        multiple
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        accept=".csv,.xlsx,.xls,.s3db,.sqlite,.zip"
      />

      {/* ✅ COLLAPSIBLE CONTROLS (mobile) */}
      <div className={styles.controlsLabelframe} data-tut="anomaly-controls">
        <div className={styles.controlsHeader}>
          <div className={styles.controlsTitle}>Aktionen</div>
          <button
            type="button"
            className={styles.controlsToggle}
            onClick={() => setIsControlsOpen(v => !v)}
            aria-expanded={isControlsOpen ? 'true' : 'false'}
            aria-label={isControlsOpen ? 'Aktionen einklappen' : 'Aktionen ausklappen'}
          >
            {isControlsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </button>
        </div>

        <div className={`${styles.controlsBody} ${isControlsOpen ? styles.controlsBodyOpen : styles.controlsBodyClosed}`}>
          <button onClick={handleImportClick} disabled={isLoading} data-tut="anomaly-import-button">
            <FileUploadIcon style={iconStyle} />
            Dateien importieren
          </button>

          <button onClick={handleSyncFromCloud} disabled={isLoading} data-tut="anomaly-sync-button">
            <CloudSyncIcon style={iconStyle} />
            Cloud-Sync
          </button>

          <button onClick={() => setMeansModalOpen(true)} disabled={!activeFile || isLoading} data-tut="anomaly-means-button">
            <CalculateIcon style={iconStyle} />
            Mittelwerte berechnen
          </button>

          <button onClick={handleOpenThresholdModal} disabled={(!activeFile && !isNewUserForTutorial) || isLoading} data-tut="anomaly-analyze-button">
            <SettingsIcon style={iconStyle} />
            {isLoading ? 'Laden...' : 'Konfigurieren & Analysieren'}
          </button>

          <button onClick={handleOpenAnalysisModal} disabled={isLoading} data-tut="anomaly-analyze-sensors-button">
            <ScienceIcon style={iconStyle} />
            Sensoren analysieren
          </button>

          <div className={styles.progressContainer}>
            <progress value={progress} max="100" className={styles.progressBar}></progress>
          </div>

          <button onClick={handleDownloadReport} disabled={!analysisResult?.plots?.length || isLoading} data-tut="anomaly-pdf-button">
            <PictureAsPdfIcon style={iconStyle} />
            PDF-Bericht
          </button>

          <button onClick={handleAutoGenerateExcelReport} disabled={loadedFiles.length === 0 || isLoading} data-tut="anomaly-excel-button">
            <ArticleIcon style={iconStyle} />
            Excel-Bericht (Dashboard)
          </button>
        </div>
      </div>

      <div className={styles.contentPane}>
        <div
          ref={leftPanelRef}
          className={styles.machinesLabelframe}
          style={{width: window.matchMedia("(max-width: 900px)").matches ? "100%" : `${leftPanelWidth}px`,}}
          data-tut="anomaly-file-list"
        >
          <h3>Importierte Dateien</h3>
          <div className={styles.searchContainer} data-tut="anomaly-file-search">
            <input type="text" placeholder="Dateien suchen..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
            <SearchIcon className={styles.searchIcon} />
          </div>
          <div className={styles.machineListFrame}>
            {isNewUserForTutorial && (
              <button
                key="fake-tutorial-file"
                className={`${styles.fileButton} ${styles.active}`}
                data-tut="anomaly-file-item"
                onClick={() => {
                  setActiveFile({ name: 'sample_data.csv', fileObject: null });
                  setStatusMessage('Aktive Datei: sample_data.csv (Tutorial)');
                  const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="anomaly-file-item"]';
                  if (isTutorialTrigger) {
                    goToNextStep();
                  }
                }}
              >
                <span>sample_data.csv</span>
              </button>
            )}

            {filteredFiles.map((file, index) => (
              <div
                key={file.name}
                className={`${styles.fileButton} ${activeFile?.name === file.name ? styles.active : ''}`}
                onClick={() => {
                  selectFile(file);
                  const isTutorialTrigger = isTutorialActive && joyrideState.steps[joyrideState.stepIndex]?.target === '[data-tut="anomaly-file-item"]';
                  if (isTutorialTrigger) {
                    goToNextStep();
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    selectFile(file);
                  }
                }}
                role="button"
                tabIndex={0}
                title={file.name}
                data-tut={(isTutorialActive && !isNewUserForTutorial && index === 0) ? "anomaly-file-item" : undefined}
              >
                <span>{file.name}</span>
                <button
                  type="button"
                  className={styles.deleteFileIcon}
                  onClick={(e) => handleDeleteFile(e, file)}
                  title={`Lösche ${file.name}`}
                  aria-label={`Lösche ${file.name}`}
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div
          className={styles.resizer}
          onMouseDown={handleResizeMouseDown}
        />

        <div className={styles.plotLabelframe} data-tut="anomaly-plot-labelframe">
          <h3>Anomalie-Plots</h3>
          <div 
            className={`${styles.plotSelectionControls} ${showPlotControls ? styles.controlsVisible : styles.controlsHidden}`} 
            data-tut="anomaly-plot-controls"
          >
            {/* ... the buttons inside remain the same ... */}
            <button
              onClick={handleSelectAllAnomalies}
              disabled={!analysisResult?.plots?.length || isLoading}
            >
              <SelectAllIcon style={smallIconStyle} />
              Alle auswählen
            </button>
            <button
              onClick={handleClearSelection}
              disabled={selectedSensors.size === 0 || isLoading}
            >
              <DeselectIcon style={smallIconStyle} />
              Auswahl aufheben
            </button>
          </div>
          <div className={styles.plotArea} onScroll={handlePlotAreaScroll}>
            {isLoading && <div className={styles.loading}>Verarbeitung läuft...</div>}
            {error && <div className={styles.error}>{error}</div>}
            {!analysisResult && !isLoading && <p className={styles.placeholderText}>Wählen Sie eine Datei und klicken Sie auf "Konfigurieren & Analysieren", um Ergebnisse zu sehen.</p>}
            {analysisResult?.plots?.length === 0 && !isLoading && <p className={styles.placeholderText}>Keine Anomalien für die gewählten Grenzwerte gefunden.</p>}
            {analysisResult?.plots && analysisResult.plots.length > 0 && (
              <div className={styles.plotsGrid}>
                {analysisResult.plots.map((plot, index) => (
                  <div key={index} className={styles.sensorPlotContainer} onClick={() => handleSingleSensorAnalysis(plot.sensor_name)}>
                    <div
                      className={styles.plotHeader}
                      title={plot.sensor_name}
                    >
                      <label className={styles.checkboxLabel} onClick={(e) => handleSensorSelectionChange(e, plot.sensor_name)}>
                        <input type="checkbox" checked={selectedSensors.has(plot.sensor_name)} readOnly style={{ display: 'none' }} />

                        {selectedSensors.has(plot.sensor_name) ?
                          <CheckBoxIcon style={{ marginRight: '8px', color: 'var(--accent-color)' }} /> :
                          <CheckBoxOutlineBlankIcon style={{ marginRight: '8px', color: 'var(--text-color-light)' }} />
                        }
                        {mergedTranslations[plot.sensor_name] || plot.sensor_name} analysieren
                      </label>
                    </div>
                    <div className={styles.plotsWrapper}>
                      <div className={styles.mainPlotCard}>
                        <LazyLoadImage
                          alt={`Plot für ${plot.sensor_name}`}
                          src={plot.image_data}
                          effect="blur"
                          width="100%"
                        />
                      </div>
                      {plot.zoom_imgs && plot.zoom_imgs.length > 0 && (
                        <div className={styles.zoomPlotsContainer}>
                          {plot.zoom_imgs.map((zoom_img, z_index) => (
                            <LazyLoadImage
                              key={z_index}
                              alt={`Zoom-Plot für ${plot.sensor_name}`}
                              src={zoom_img}
                              className={styles.zoomPlotImage}
                              effect="blur"
                              width="100%"
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className={styles.statusBar}>{statusMessage}</div>

      <Dialog
        open={!!deleteDialogFile}
        onClose={() => setDeleteDialogFile(null)}
        PaperProps={{ style: dialogPaperStyle }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'var(--danger-color)' }}>
          Datei löschen?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'var(--text-soft)' }}>
            {deleteDialogFile ? `Möchten Sie ${deleteDialogFile.name} wirklich löschen?` : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setDeleteDialogFile(null)}
            sx={{ color: 'var(--text-muted)', fontWeight: 600, textTransform: 'none' }}
          >
            Abbrechen
          </Button>
          <Button
            onClick={confirmDeleteFile}
            variant="contained"
            sx={{
              bgcolor: 'var(--danger-color)',
              '&:hover': { bgcolor: 'var(--danger-color-strong)' },
              borderRadius: '8px',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 10px 24px var(--danger-soft-bg)'
            }}
            autoFocus
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={customMessageDialog.open}
        onClose={() => setCustomMessageDialog({ open: false, title: '', message: '' })}
        PaperProps={{ style: dialogPaperStyle }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: 'var(--accent-color)' }}>
          {customMessageDialog.title}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'var(--text-soft)', whiteSpace: 'pre-line' }}>
            {customMessageDialog.message}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setCustomMessageDialog({ open: false, title: '', message: '' })}
            variant="contained"
            sx={{
              bgcolor: 'var(--accent-color)',
              '&:hover': { bgcolor: 'var(--accent-color-strong)' },
              borderRadius: '8px',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: '0 10px 24px var(--accent-soft-bg)'
            }}
            autoFocus
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default AnomalyDetectionInterface;